/**
 * Box 5, the breakdown (spec §3.4, R16, R17). It holds:
 * - Naming coverage with its chip;
 * - the four tabs, with A ("เฉพาะ campaign ที่ Active", plus "(ซ่อน N)" while A is on) beside them;
 * - the open tab.
 *
 * The card fails whole only with the entities (R8). One tab whose read failed
 * says so with the retry (R9), never 0; each tab draws its state with
 * OverviewBox's own BoxBody (P11), so a tab and a box cannot look different.
 * Ad format and Creative are ad grain, so they say how much of the spend they
 * hold (R17); with no ad row at all (Google PMax) that line is followed by
 * the empty line, never by a table of dashes (spec §2.1, D1 R16, P10). The tab
 * and A belong to the page (the URL): a click asks, the card never decides.
 *
 * Data attributes the live check reads (raw, '' for null): data-naming-coverage,
 * data-hidden, data-ad-share, data-format-share.
 *
 * @module components/channels/BreakdownCard
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BoxMessage } from '@/components/looker/app/appBoxShared';
import { BoxBody, OverviewBox } from '@/components/overview/OverviewBox';
import { Chip } from '@/components/overview/OverviewTable';
import { fmtNamingCoverage } from '@/services/looker/lookerCoverage';
import { fmtPct } from '@/services/overview/overviewFormat';
import type { BoxState } from '@/services/overview/overviewTypes';
import {
  BREAKDOWN_TABS, type AdChannel, type AdGrainCoverage, type BreakdownTab, type BreakdownView,
  type AdGrainTable, type NamingCoverage, type TargetingBreakdown,
} from '@/services/channels/channelTypes';
import type { TFn } from '@/services/channels/channelFormat';
import { CampaignTable, CreativeTable, FormatTable, TargetingTable } from './BreakdownTables';
import { SegmentCharts } from './SegmentCharts';

const raw = (n: number | null): string => (n === null ? '' : String(n));

/** Literal keys, so the locale integrity test sees them. */
const tabLabel = (tab: BreakdownTab, t: TFn): string => {
  if (tab === 'campaign') return t('channels.breakdown.tabs.campaign');
  if (tab === 'format') return t('channels.breakdown.tabs.format');
  if (tab === 'targeting') return t('channels.breakdown.tabs.targeting');
  return t('channels.breakdown.tabs.creative');
};

/**
 * "Naming coverage X%", floored as D1 box 8 prints it (fmtNamingCoverage, rules-04 E2): "ครบ" at 100%, "มีชื่อที่แยกไม่ได้"
 * below it. No shown campaign spent: "—" and no chip. Before the facts: nothing.
 */
const NamingCoverageLine: React.FC<{ coverage: NamingCoverage | null }> = ({ coverage }) => {
  const { t } = useTranslation();
  if (coverage === null) return null;
  const { share } = coverage;
  return (
    <p data-naming-coverage={raw(share)} className="text-sm text-neutral-800">
      {t('channels.breakdown.namingCoverage', { pct: fmtNamingCoverage(share) })}
      {share === 1 && (
        <span className="ml-2 inline-block whitespace-nowrap rounded-full border border-success-700 px-1.5 text-[11px] text-success-700">
          {t('channels.breakdown.namingComplete')}
        </span>
      )}
      {share !== null && share < 1 && <Chip text={t('channels.breakdown.namingIncomplete')} />}
    </p>
  );
};

/**
 * A, from the URL. "(ซ่อน N)" shows only while A is on and N > 0 (R37): with A off nothing is hidden, so the label
 * would be false. data-hidden carries the raw count in both states (the live check reads it).
 */
const ActiveToggle: React.FC<{ on: boolean; hidden: number | null; onActive: (on: boolean) => void }> = ({ on, hidden, onActive }) => {
  const { t } = useTranslation();
  return (
    <label data-hidden={raw(hidden)} className="inline-flex items-center gap-2 text-sm text-neutral-900">
      <input type="checkbox" checked={on} onChange={(e) => onActive(e.target.checked)} className="h-4 w-4 rounded border-neutral-400" />
      <span>{t('channels.breakdown.activeOnly')}</span>
      {on && hidden !== null && hidden > 0 && <span className="text-neutral-700">{t('channels.breakdown.hidden', { n: hidden })}</span>}
    </label>
  );
};

interface TabsProps {
  tab: BreakdownTab;
  onTab: (tab: BreakdownTab) => void;
  idOf: (tab: BreakdownTab) => string;
  panelId: string;
}

const BreakdownTabs: React.FC<TabsProps> = ({ tab, onTab, idOf, panelId }) => {
  const { t } = useTranslation();
  return (
    <div role="tablist" aria-label={t('channels.box.breakdown')} className="inline-flex flex-wrap gap-1 rounded-xl bg-neutral-100 p-1">
      {BREAKDOWN_TABS.map((k) => (
        <button
          key={k} id={idOf(k)} type="button" role="tab" aria-selected={k === tab} aria-controls={k === tab ? panelId : undefined}
          onClick={() => onTab(k)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium ${k === tab ? 'bg-white text-ink-950 shadow-sm' : 'text-neutral-800 hover:text-ink-950'}`}
        >
          {tabLabel(k, t)}
        </button>
      ))}
    </div>
  );
};

/**
 * "ยอดระดับ ad = X% ของ spend · มี format Y%". A known 0 prints "0.0%" (an ad spend with no format at all: no platform
 * value and no format word in any name, D3 §3.4); "—" for a share that cannot be measured: the ad spend is NULL or 0,
 * or formatted ads exist but none reports spend (P9, R17: an unreported numerator is not a measured 0).
 */
const AdGrainLine: React.FC<{ coverage: AdGrainCoverage }> = ({ coverage }) => {
  const { t } = useTranslation();
  return (
    <p data-ad-share={raw(coverage.adShare)} data-format-share={raw(coverage.formatShare)} className="mb-2 text-xs text-neutral-700">
      <span>{t('channels.breakdown.adShare', { pct: fmtPct(coverage.adShare, 1) })}</span>
      <span aria-hidden="true" className="mx-1">·</span>
      <span>{t('channels.breakdown.formatShare', { pct: fmtPct(coverage.formatShare, 1) })}</span>
    </p>
  );
};

/**
 * An ad-grain tab (Ad format, Creative): the coverage line, then its table. With no ad row (the ad sets spent and no ad
 * exists: Google PMax) the line stands over the empty line instead of a table whose only row is "รวม" of dashes (P10).
 */
const AdGrainBody: React.FC<{ table: AdGrainTable<unknown>; children: React.ReactNode }> = ({ table, children }) => {
  const { t } = useTranslation();
  return (
    <>
      <AdGrainLine coverage={table.coverage} />
      {table.rows.length === 0 ? <BoxMessage text={t('looker.tileEmpty')} /> : children}
    </>
  );
};

interface BodyProps {
  platform: AdChannel;
  currency: string | null;
}

/** Google targets by keyword / affinity, not by ad-group name: the line instead of charts (S6.1). */
const TargetingBody: React.FC<BodyProps & { table: TargetingBreakdown }> = ({ platform, table, currency }) => {
  const { t } = useTranslation();
  const charts = platform === 'google'
    ? <p className="text-xs text-neutral-700">{t('channels.breakdown.googleNoSegments')}</p>
    : table.segments !== null && table.segments.length > 0 && <SegmentCharts segments={table.segments} currency={currency} />;
  return (
    <div className="space-y-3">
      {charts}
      <TargetingTable platform={platform} table={table} currency={currency} />
    </div>
  );
};

const TabBody: React.FC<BodyProps & { view: BreakdownView; tab: BreakdownTab; onRetry?: () => void }> = ({ platform, currency, view, tab, onRetry }) => {
  if (tab === 'campaign') {
    return <BoxBody state={view.campaign} onRetry={onRetry}>{(table) => <CampaignTable platform={platform} table={table} currency={currency} />}</BoxBody>;
  }
  if (tab === 'format') {
    return (
      <BoxBody state={view.format} onRetry={onRetry}>
        {(table) => <AdGrainBody table={table}><FormatTable platform={platform} table={table} currency={currency} /></AdGrainBody>}
      </BoxBody>
    );
  }
  if (tab === 'targeting') {
    return <BoxBody state={view.targeting} onRetry={onRetry}>{(table) => <TargetingBody platform={platform} table={table} currency={currency} />}</BoxBody>;
  }
  return (
    <BoxBody state={view.creative} onRetry={onRetry}>
      {(table) => <AdGrainBody table={table}><CreativeTable platform={platform} table={table} currency={currency} /></AdGrainBody>}
    </BoxBody>
  );
};

interface Props {
  platform: AdChannel;
  state: BoxState<BreakdownView>;
  tab: BreakdownTab;
  onTab: (tab: BreakdownTab) => void;
  onActive: (on: boolean) => void;
  currency: string | null;
  onRetry?: () => void;
}

export const BreakdownCard: React.FC<Props> = ({ platform, state, tab, onTab, onActive, currency, onRetry }) => {
  const { t } = useTranslation();
  const base = React.useId();
  const idOf = (k: BreakdownTab): string => `${base}-tab-${k}`;
  const panelId = `${base}-panel`;
  return (
    <OverviewBox id="5" title={t('channels.box.breakdown')} state={state} onRetry={onRetry}>
      {(view) => (
        <div className="space-y-3">
          <NamingCoverageLine coverage={view.namingCoverage} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <BreakdownTabs tab={tab} onTab={onTab} idOf={idOf} panelId={panelId} />
            <ActiveToggle on={view.activeOnly} hidden={view.hidden} onActive={onActive} />
          </div>
          <div role="tabpanel" id={panelId} aria-labelledby={idOf(tab)}>
            <TabBody platform={platform} currency={currency} view={view} tab={tab} onRetry={onRetry} />
          </div>
        </div>
      )}
    </OverviewBox>
  );
};
